import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GraduationCap, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, setToken } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogoLockup } from "@/components/Logo";
import { toast } from "sonner";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <LogoLockup />
        </Link>
        <div className="bg-white border border-border rounded-2xl shadow-sm p-8">
          <h1 className="font-heading text-2xl font-bold text-center">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground text-center mt-1.5">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function googleLoginRedirect() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton({ label = "Continue with Google", testId = "google-login-btn" }) {
  return (
    <Button type="button" variant="outline" onClick={googleLoginRedirect}
      className="w-full rounded-full h-11 font-semibold border-2" data-testid={testId}>
      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 0 0-9.82 6.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
      {label}
    </Button>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <span className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><span className="h-px flex-1 bg-border" />
    </div>
  );
}

export function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(""), [password, setPassword] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav("/"); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(email, password); toast.success("Welcome back!"); nav("/"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" subtitle="Log in to continue learning">
      <GoogleButton />
      <OrDivider />
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" placeholder="you@example.com" /></div>
        <div><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" placeholder="••••••••" /></div>
        <div className="text-right"><Link to="/forgot-password" className="text-sm text-teal hover:underline" data-testid="forgot-link">Forgot password?</Link></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="login-submit">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Log in
        </Button>
      </form>
      <p className="text-center text-sm text-muted-foreground mt-6">
        New here? <Link to="/register" className="text-teal font-medium hover:underline" data-testid="goto-register">Create an account</Link>
      </p>
    </Shell>
  );
}

export function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(""), [email, setEmail] = useState(""), [password, setPassword] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav("/"); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await register(name, email, password); toast.success("Account created! Check your email to verify."); nav("/"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" subtitle="Start learning how to get clients on Upwork">
      <GoogleButton label="Sign up with Google" testId="google-register-btn" />
      <OrDivider />
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Full name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" placeholder="Jane Dela Cruz" /></div>
        <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" placeholder="you@example.com" /></div>
        <div><Label>Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" placeholder="At least 6 characters" /></div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="register-submit">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create account
        </Button>
      </form>
      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account? <Link to="/login" className="text-teal font-medium hover:underline" data-testid="goto-login">Log in</Link>
      </p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""), [busy, setBusy] = useState(false), [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Reset your password" subtitle="We'll email you a reset link">
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <CheckCircle2 className="w-12 h-12 text-brandgreen mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">If an account exists for <b>{email}</b>, a reset link is on its way.</p>
          <Link to="/login" className="text-teal text-sm font-medium hover:underline block mt-4">Back to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="forgot-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send reset link
          </Button>
          <Link to="/login" className="text-teal text-sm font-medium hover:underline block text-center">Back to login</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [password, setPassword] = useState(""), [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password updated. Please log in."); nav("/login"); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>New password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" /></div>
        <Button type="submit" disabled={busy || !token} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="reset-submit">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Update password
        </Button>
      </form>
    </Shell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState("loading");
  useEffect(() => {
    (async () => {
      try { await api.post("/auth/verify-email", { token }); setState("ok"); }
      catch { setState("fail"); }
    })();
  }, [token]);
  return (
    <Shell title="Email verification">
      <div className="text-center" data-testid="verify-result">
        {state === "loading" && <Loader2 className="w-12 h-12 text-teal mx-auto animate-spin" />}
        {state === "ok" && (<><CheckCircle2 className="w-12 h-12 text-brandgreen mx-auto mb-3" /><p className="text-muted-foreground">Your email is verified! You're all set.</p></>)}
        {state === "fail" && (<><XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" /><p className="text-muted-foreground">This verification link is invalid or expired.</p></>)}
        <Link to="/" className="text-teal font-medium hover:underline block mt-4">Go to Home</Link>
      </div>
    </Shell>
  );
}


export function AuthCallback() {
  const { googleAuth } = useAuth();
  const nav = useNavigate();
  const done = React.useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      if (!sid) { nav("/login"); return; }
      try {
        await googleAuth(sid);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("Signed in with Google");
        nav("/");
      } catch {
        toast.error("Google sign-in failed. Please try again.");
        nav("/login");
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Shell title="Signing you in…" subtitle="One moment while we verify your Google account">
      <div className="text-center py-6"><Loader2 className="w-10 h-10 text-teal mx-auto animate-spin" /></div>
    </Shell>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite-info?token=${token}`);
        setInfo(data); setName(data.name || ""); setState("ok");
      } catch { setState("fail"); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      if (data.access_token) setToken(data.access_token);
      setUser(data);
      toast.success("Welcome to the team! Your account is active.");
      nav("/admin");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Shell title="Accept your invitation" subtitle={info ? `Joining as ${info.role}` : ""}>
      {state === "loading" && <Loader2 className="w-10 h-10 text-teal mx-auto animate-spin" />}
      {state === "fail" && (
        <div className="text-center" data-testid="invite-invalid">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-muted-foreground">This invitation is invalid or has expired. Please ask your admin to resend it.</p>
        </div>
      )}
      {state === "ok" && (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input value={info.email} disabled data-testid="invite-email" /></div>
          <div><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name" /></div>
          <div><Label>Set a password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 6 characters" data-testid="invite-password" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-teal hover:bg-teal/90" data-testid="invite-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Activate my account
          </Button>
        </form>
      )}
    </Shell>
  );
}
